/* eslint-disable consistent-return */
import ReactECharts from 'echarts-for-react'
import echarts from 'echarts/lib/echarts'
import { groupBy, isNil, isUndefined, max, maxBy, min, minBy, round, uniq, uniqBy } from 'lodash'
import moment from 'moment'
import PropTypes from 'prop-types'
import React, { useRef, useState } from 'react'
import i18n from 'simple-react-i18n'
import { getDate, getFullDate } from 'utils/DateUtil'
import { exportFile } from 'utils/ExportDataUtil'
import {
    exportExcelIcon, quantificationIcon,
    exportPictureIcon, getAxisIntervalFormatter, logIcon, setLogOptions, setYOptions, thresholdIcon, toEchartsData, yAutomaticScaleValues,
} from '../../../../../components/echart/EChartUtils'
import { shallowEqual, useSelector } from 'react-redux'
import { getColorFromPalette, getThresholdColor } from 'utils/ColorUtil'
import useLocalStorage from 'utils/customHook/useLocalStorage'
import { CHART_DISPLAY } from 'quality/constants/QualityConstants'
import { greyBlue, grey } from 'utils/constants/ColorTheme'
import { getVirtualStationBandSeries, isVirtualStationBand } from '../../../../../station/components/indicator/VirtualStationUtils'
import ReturnPeriodLegend from '../../../../../station/components/indicator/ReturnPeriodLegend'
import { SCORE_AXIS } from '../../../../../import/constants/IndicatorsConstants'
import { getReturnPeriodSeries } from '../../../../../station/components/indicator/ReturnPeriodUtils'
import { HYDRO_TYPEID } from 'hydrometry/constants/HydrometryConstants'

const DEFAULT_GRAPH_HEIGHT = 150
const DEFAULT_GAP = 50
const DEFAULT_HEADER_HEIGHT = 50
const DEFAULT_FOOTER_HEIGHT = 75

const HydroChart = ({
    dataMeasures = [],
    startDate,
    endDate,
    virtualStation = {},
    virtualStationUnit = '',
    returnPeriodMode = false,
    returnPeriodCurves = [],
    title = i18n.hydroMonitoringCatchment,

    headerHeight = DEFAULT_HEADER_HEIGHT,
    footerHeight = DEFAULT_FOOTER_HEIGHT,
}) => {
    const {
        hydrometricStations,
        hydrometryThresholds,
        hydrometryDataTypes,
    } = useSelector(store => ({
        hydrometricStations: store.HydrometryReducer.hydrometricStations,
        hydrometryThresholds: store.HydrometryReducer.hydrometryThresholds,
        hydrometryDataTypes: store.HydrometryReducer.hydrometryDataTypes,
    }), shallowEqual)

    // use on mouse event
    let echartRef = useRef(undefined)
    let displayToolbox = useRef(false)

    const [displayLog, setDisplayLog] = useState(false)
    const [chartDisplay, setChartDisplay] = useLocalStorage(CHART_DISPLAY, {})
    const [formatQuantification, setFormatQuantification] = useState(chartDisplay.formatQuantification)
    const [displayThresholds, setDisplayThresholds] = useState(chartDisplay.displayThresholds ?? true)

    const minDate = startDate ?? min(dataMeasures.map(d => minBy(d.measures, 'date')?.date).filter(d => !isUndefined(d)))
    const maxDate = endDate ?? max(dataMeasures.map(d => maxBy(d.measures, 'date')?.date).filter(d => !isUndefined(d)))

    const {
        formatter,
        interval,
    } = getAxisIntervalFormatter(moment(maxDate), moment(minDate))

    const hasVirtualStation = !!virtualStation.measures?.length
    const gridOffset = hasVirtualStation ? 1 : 0

    const listDataTypes = uniqBy(dataMeasures, 'dataType').map(m => m.dataType)

    const heightStationIds = returnPeriodMode
        ? uniq(dataMeasures.filter(m => m.dataType === HYDRO_TYPEID.HEIGHT).map(m => m.stationId))
        : []

    const dataTypeGrids = listDataTypes.flatMap(dataType => (returnPeriodMode && dataType === HYDRO_TYPEID.HEIGHT)
        ? heightStationIds.map(stationId => ({ dataType, stationId }))
        : [{ dataType }])

    const allGrids = hasVirtualStation ? [{ isVirtualStation: true }, ...dataTypeGrids] : dataTypeGrids

    const getGridIndex = (dataType, stationId) => {
        const found = dataTypeGrids.findIndex(g => g.dataType === dataType && (isNil(g.stationId) || g.stationId === stationId))
        return (found === -1 ? 0 : found) + gridOffset
    }

    const virtualStationSeries = !hasVirtualStation ? [] : [
        ...getVirtualStationBandSeries(virtualStation.bands, 0),
        {
            name: i18n.virtualStation,
            type: 'line',
            data: virtualStation.measures.map(([date, value]) => ({ value: [date, value] })),
            showSymbol: false,
            connectNulls: false,
            color: '#1d3b6e',
            lineStyle: { width: 2 },
            xAxisIndex: 0,
            yAxisIndex: 0,
        },
    ]

    const series = listDataTypes.flatMap(dt => {
        const dataType = hydrometryDataTypes.find(p => p.id === dt)
        const measures = dataMeasures.filter(m => m.dataType === dt)
        return measures.map((m, mi) => {
            const hydro = hydrometricStations.find(p => p.id === m.stationId)
            const thresholds = !displayThresholds ? [] : hydrometryThresholds.filter(t => !isUndefined(t.value) && t.stationId == m.stationId && t.dataType == m.dataType).map(t => ({
                value: t.value,
                title: t.title,
                color: t.htmlColor || getThresholdColor(t.color),
            }))
            return {
                name: `${hydro?.name ?? ''} (${dataType?.label ?? ''})`,
                type: 'line',
                data: toEchartsData(displayLog ? m.measures.filter(d => d.value > 0) : m.measures),
                markLine: {
                    silent: false,
                    data: thresholds.map(t => ({
                        yAxis: t.value,
                        symbol: 'none',
                        label: {
                            show: true,
                            position: 'middle',
                            formatter: () => t.title ? `${t.title} : ${round(t.value, 5)}` : '',
                        },
                        lineStyle: {
                            normal: {
                                color: t.color,
                                type: 'dashed',
                            },
                        },
                    })),
                },

                connectNulls: false,
                yAxisIndex: getGridIndex(dt, m.stationId),
                xAxisIndex: getGridIndex(dt, m.stationId),
                color: getColorFromPalette(mi),
                lineStyle: {
                    type: dataType?.lineType ?? 'solid',
                    opacity: dataType?.lineOpacity ?? 1,
                    width: dataType?.lineWidth ?? 2,
                },
            }
        })
    })

    const legendData = [...(hasVirtualStation ? [i18n.virtualStation] : []), ...series.map(({ name }) => name)]

    const grids = allGrids.map(({ dataType: dt, stationId }, i) => {
        if (hasVirtualStation && i === 0) {
            return {
                id: undefined,
                name: virtualStationUnit,
                gridOptions: { top: 0 },
                isVirtualStation: true,
            }
        }
        const dataType = hydrometryDataTypes.find(p => p.id === dt)
        const typeLabel = dataType ? `${dataType.label ?? ''} ${dataType.unit ? `[${dataType.unit}]` : ''}` : i18n.unknown
        const stationName = isNil(stationId) ? undefined : hydrometricStations.find(h => h.id === stationId)?.name
        return {
            id: dt,
            stationId,
            name: stationName ? `${stationName} - ${typeLabel}` : typeLabel,
            gridOptions: {
                top: i === 0 ? 0 : undefined,
            },
        }
    })

    const curvesByStation = groupBy(returnPeriodCurves, 'stationId')

    const returnPeriodBands = grids.flatMap(({ stationId }, i) => isNil(stationId)
        ? []
        : getReturnPeriodSeries(curvesByStation[stationId] ?? [], i, false))

    let tmpGap = headerHeight
    const grid = allGrids.map((_, i) => {
        const tmpTop = tmpGap + (i === 0 ? 0 : DEFAULT_GAP)
        const tmpGrid = {
            top: tmpTop,
            height: DEFAULT_GRAPH_HEIGHT,
            left: '70px',
            right: '40px',
        }
        tmpGap = tmpTop + DEFAULT_GRAPH_HEIGHT
        return tmpGrid
    })

    const xAxis = allGrids.map((_, i) => {
        return {
            type: 'time',
            boundaryGap: true,
            axisLabel: {
                rotate: 0,
                formatter,
                show: true,
            },
            axisLine: { show: true },
            axisTick: { show: true },
            splitLine: { show: true },
            interval,
            gridIndex: i,
            min: minDate,
            max: maxDate,
        }
    })

    const yAxis = grids.map((dt, index) => {
        if (dt.isVirtualStation) {
            return {
                type: 'value',
                nameLocation: 'middle',
                name: virtualStationUnit,
                gridIndex: 0,
                nameGap: 40,
                showSplitLine: true,
                ...setYOptions(null, SCORE_AXIS),
            }
        }
        const filteredData = series.filter(d => d.yAxisIndex === index)
        const allMeasures = filteredData.flatMap(d => d.data?.map(m => m.value[1]) ?? [])
        const allThresholds = filteredData.flatMap(d => d.markLine.data?.map(t => t.yAxis) ?? [])
        const measuresWithQuantification = formatQuantification ? [...allMeasures, ...allThresholds, 0].filter(v => typeof v === 'number' && v >= 0) : [...allMeasures, ...allThresholds]
        const yScale = yAutomaticScaleValues(measuresWithQuantification)
        return {
            type: displayLog ? 'log' : 'value',
            nameLocation: 'middle',
            name: dt.name,
            gridIndex: index,
            nameGap: 40,
            showSplitLine: true,
            ...setYOptions(null, yScale),
            ...setLogOptions(displayLog),
        }
    })

    const getLegend = () => ({
        top: 20,
        left: 70,
        right: displayToolbox.current ? `${30 * 4 + 35}px` : 40,
        type: 'scroll',
        show: true,
        data: legendData,
    })

    const getToolbox = () => ({
        top: 5,
        right: 40,
        showTitle: false,
        itemSize: 18,
        tooltip: {
            show: true,
            position: 'bottom',
        },
        feature: displayToolbox.current ? {
            myLog: {
                title: i18n.logarithm,
                icon: logIcon,
                onclick: () => setDisplayLog(p => !p),
            },
            myToolQuantification: {
                show: true,
                title: i18n.displayYAxis0,
                icon: quantificationIcon,
                onclick: () => {
                    setFormatQuantification(prevDisplayLog => !prevDisplayLog)
                    setChartDisplay({ ...chartDisplay, displayLog: false, formatQuantification: !formatQuantification })
                },
                iconStyle: {
                    borderColor: formatQuantification ? greyBlue : grey,
                },
            },
            myToolThreshold: {
                show: !!hydrometryThresholds.length,
                title: i18n.toggleThreshold,
                icon: thresholdIcon,
                onclick: () => {
                    const next = !displayThresholds
                    setDisplayThresholds(next)
                    setChartDisplay({ ...chartDisplay, displayThresholds: next })
                },
                iconStyle: {
                    borderColor: displayThresholds ? greyBlue : grey,
                },
            },
            saveAsImage: {
                title: i18n.pictureExport,
                icon: exportPictureIcon,
                name: i18n.dataHydro,
            },
            myToolExport: {
                title: i18n.excelExport,
                icon: exportExcelIcon,
                onclick: () => {
                    const exportData = dataMeasures.flatMap(dm => {
                        const hydro = hydrometricStations.find(({ id }) => id === dm.stationId)
                        const dt = hydrometryDataTypes.find(h => h.id === dm.dataType)
                        return dm.measures.map(m => ({
                            code: hydro?.code,
                            date: { value: getDate(m.date), format: 'dd/MM/yyyy', cellType: 'date' },
                            value: { value: m.value, format: '0.00000', cellType: 'number' },
                            name: hydro?.name || dm.stationId,
                            dataType: dt?.name ?? i18n.unknown,
                        }))
                    })
                    exportFile({
                        data: exportData.length ? [
                            {
                                ...exportData[0],
                                headers: ['code', 'name', 'dataType', 'date', 'value'],
                            },
                            ...exportData.slice(1),
                        ] : [],
                        exportType: 'xlsx',
                        titleFile: i18n.dataHydro,
                    }, true)
                },
            },
            restore: { title: i18n.restore },
        } : {},
    })

    const options = {
        title: {
            text: title,
            left: 'center',
        },
        series: [...virtualStationSeries, ...returnPeriodBands, ...series],
        legend: getLegend(),
        xAxis,
        yAxis,
        tooltip: {
            trigger: 'axis',
            formatter: params => {
                const measures = params.filter(o => !isVirtualStationBand(o.seriesName) && !o.data?.isReturnPeriod)
                if (!measures.length) {
                    return ''
                }
                const date = getFullDate(measures[0].value[0])
                const result = measures.map(o => `${o.marker} ${o.seriesName} : ${round(o.value[1], 2)} ${o.unit ?? ''}`).join('<br />')
                return `${date} <br /> ${result}`
            },
        },
        grid,
        dataZoom: [{
            type: 'slider',
            xAxisIndex: grids.map((_, index) => index),
            filterMode: 'none',
        }, {
            bottom: '10px',
            type: 'inside',
            filterMode: returnPeriodBands.length ? 'none' : 'filter',
            xAxisIndex: grids.map((_, index) => index),
            handleStyle: {
                color: '#fff',
                shadowBlur: 3,
                shadowColor: 'rgba(0, 0, 0, 0.6)',
                shadowOffsetX: 2,
                shadowOffsetY: 2,
            },
        }],
        toolbox: getToolbox(),
        backgroundColor: '#fff',
    }

    const componentHeight = grids.reduce((acc, { gridOptions: { height = DEFAULT_GRAPH_HEIGHT, top = DEFAULT_GAP } = {} }) => acc + height + top, headerHeight + footerHeight)

    return (
        <div
            onMouseOver={() => {
                displayToolbox.current = true
                echartRef.current?.getEchartsInstance().setOption({
                    ...options,
                    legend: getLegend(),
                    toolbox: getToolbox(),
                }, true, true)
            }}
            onMouseOut={() => {
                displayToolbox.current = false
                echartRef.current?.getEchartsInstance().setOption({
                    ...options,
                    legend: getLegend(),
                    toolbox: getToolbox(),
                }, true, true)
            }}
        >
            <ReactECharts
                echarts={echarts}
                option={options}
                notMerge={true}
                lazyUpdate={true}
                style={{ height: componentHeight, paddingTop: '20px' }}
                ref={e => {
                    echartRef.current = e
                }}
            />
            {(hasVirtualStation || !!returnPeriodBands.length) && <ReturnPeriodLegend />}
        </div>
    )
}

HydroChart.propTypes = {
    dataMeasures: PropTypes.arrayOf(PropTypes.shape({

    })),
    startDate: PropTypes.number,
    endDate: PropTypes.number,
    virtualStation: PropTypes.shape({
        measures: PropTypes.arrayOf(PropTypes.arrayOf(PropTypes.number)),
        bands: PropTypes.shape({}),
    }),
    virtualStationUnit: PropTypes.string,
    returnPeriodMode: PropTypes.bool,
    returnPeriodCurves: PropTypes.arrayOf(PropTypes.shape({})),
    title: PropTypes.string,

    headerHeight: PropTypes.number,
    footerHeight: PropTypes.number,
}

export default HydroChart